import { useState } from 'react';
import type { FillAllocationDto, FillLineDto, OfficerSummaryDto, PostAudience } from '@pams/shared';
import { AddresseeCodes } from '@/components/AddresseeCodes';
import { StatusDot } from '@/components/StatusDot';
import type { FillCursor } from '@/lib/fillCursor';
import {
  UNIT_LINE_CHIP_CAP,
  allocationMayBeSplit,
  postHasRemainder,
  postHeadingCounts,
  postSummary,
} from '@/lib/fillPost';
import { postDotTone, presenceWord } from '@/lib/statusPresentation';
import { allocationClosedShort, closedShortRowNote } from '@/lib/shortfallNote';
import { POST_COPY } from './copy';
import { OfficerHoverCard, OFFICER_HOVER_CARD_ID } from './OfficerHoverCard';
import { PostModeControl, type PostMode } from './PostModeControl';
import { SlotChip } from './SlotChip';
import type { FillControls } from '@/lib/fillPermissions';
import { cn } from '@/lib/utils';
import { Lock, Van } from 'lucide-react';

/**
 * ⚠️ [amended UX1 §1a] THE SCOPE CHIP *IS* THE INDENT NOW, AND `SLOT_INDENT` IS DELETED.
 *
 * [C1h §3d] put the slots on a second line behind a fixed padding and called the chip's width
 * and that padding "one measurement stated twice". They were two measurements and the second
 * was wrong: `w-40` is 10rem and `gap-3` is 0.75rem, which is 10.75rem, and the padding said
 * 11.5rem. Nothing caught it, because `fillGaps.spec.ts` asserts every grid shares ONE left x
 * rather than a particular one — so the columns lined up with each other and with nothing else.
 *
 * §1a moves the chip down onto the slots line, where it OCCUPIES the column it used to
 * describe. The alignment is then structural: there is no number left to keep in step, the
 * empty band [C1h] left beside the grid is filled, and the grid gains 12px at every rung.
 *
 * ⚠️ THE 560px THRESHOLD SURVIVES, MOVED ONTO THE `flex-row` SWITCH. It says the same thing it
 * always said — below it there is no room for a 160px column beside a grid — and it is a
 * CONTAINER query on the row's own width, never `sm:`/`md:` (CLAUDE.md §8: `vitest.setup.ts`
 * stubs `matchMedia` to `matches: false` for ever).
 */
/*
 * ⚠️ [amended UX1.1 §2] `items-center`, NOT `items-start`.
 *
 * The chip is a 20px pill and a slot is 44px (`min-h-11`, the touch floor `SlotChip` states),
 * so top-aligning the two put the scope a full half-slot above the row it names — which reads
 * as the chip belonging to whatever is above it rather than to the slots beside it.
 *
 * ⚠️ IT CENTRES AGAINST THE WHOLE GRID, NOT THE FIRST VISUAL ROW, and that is the right answer
 * rather than a compromise: the chip names the ALLOCATION, and the allocation is every chip in
 * that grid. A twelve-slot row wrapping to two lines is still one addressee.
 *
 * ⚠️ AND IT IS HORIZONTAL ALIGNMENT `fillGaps.spec.ts` MEASURES, so this moves nothing it
 * asserts — every `fill-chips` still shares one left x, and each grid still starts to the right
 * of its own row's chip.
 */
const SLOT_ROW =
  'flex flex-col gap-2 @min-[560px]/fillline:flex-row @min-[560px]/fillline:items-center @min-[560px]/fillline:gap-3';

/** ⚠️ `w-40` IS STILL LOAD-BEARING — a shrink-to-fit chip moves the grid per row and breaks
    `fillGaps.spec.ts`'s "every slot grid starts at the same x". */
const SCOPE_CHIP =
  'inline-flex max-w-full shrink-0 truncate rounded-md bg-secondary px-2 py-0.5 text-xs text-muted-foreground @min-[560px]/fillline:w-40';

/**
 * The scrolling body of the fill screen: a section per POST, an addressee line beneath it,
 * slot chips inline.
 *
 * ⚠️ [amended C1f §1e] IT NO LONGER FLATTENS. Until C1f this mapped
 * `lines.map(line => line.allocations.map(...))` and rendered one row per ALLOCATION, so the
 * POST — which `FillLineDto` has always been — existed in the contract and nowhere on the
 * screen. Once a division can split a post across four stations, "one row per addressee" is
 * four rows with no statement of what they are four of.
 *
 * ⚠️ ONE `StatusDot` PER POST, AND IT MOVED UP RATHER THAN MULTIPLYING. The rule this file
 * has carried since [P4] — "that is the whole of the colour on this screen apart from the
 * footer's single pill" — is what makes a split ADD no colour: a post that used to be one
 * dot is still one dot, and its unit lines carry a muted count instead. A dot with words is
 * readable to a colour-blind operator and findable by `getByText`, and the second of those is
 * how the first gets caught.
 *
 * ⚠️ AND THE POST'S TONE IS `postFillStateOf`, NOT `fillStateOf` (§1g). A post delegated in
 * full with nobody named is AMBER TO OVERSIGHT — delegation is not fulfilment, and a green row
 * for an unstaffed duty is the one outcome that stops anybody chasing it.
 *
 * ⚠️ [amended UX1 §3c] …AND GREEN TO THE ADDRESSEE, WHICH IS WHY `audience` IS A PROP.
 *
 * This file's promise below was that it "never learns whose screen it is on". §3 makes that
 * false in exactly one respect and the sentence is amended rather than left contradicting the
 * code: the post's COLOUR now depends on whether the reader is the org unit the row is
 * addressed to. Everything else still holds — no query, no mutation, no scope filter, and the
 * SERVER still decides which lines exist.
 *
 * ⚠️ IT ARRIVES AS A PROP, EXACTLY AS `onPostMode` DOES, and it is REQUIRED. Deriving it here
 * from `useAuth` would put a hook into a component whose whole discipline is that it holds
 * none, and would make the fact untestable at this boundary; defaulting it would put one
 * reading in the omitted position and ship the wrong one silently, invisible to jsdom because
 * both arms render a `data-tone`.
 *
 * ⚠️ IT HOLDS NO QUERY AND NO MUTATION. Layout, keyboard and presentation only, so the same
 * component serves the divisional nodal and the CP office with no scope prop anywhere. §1c's
 * "a unit nodal sees no control" is therefore the CALLER's decision, arriving as an absent
 * `onPostMode`.
 */
export type FillLinesProps = {
  lines: readonly FillLineDto[];
  /** Keyed by `slotRef`. Empty until "copy from last time" is used. */
  staged: ReadonlyMap<string, OfficerSummaryDto>;
  cursor: FillCursor | null;
  /**
   * ⚠️ [C3c §3d] THE FIVE VERBS, NOT ONE BOOLEAN. Naming and swapping close at different
   * instants, so this row can be in a state where a chip's `Swap` is offered and its `Remove`
   * is not — see `lib/fillPermissions.ts`.
   */
  controls: FillControls;
  onAdd: (lineKey: string, allocKey: string, slotRef: string) => void;
  onSwap: (lineKey: string, allocKey: string, slotRef: string) => void;
  onRemove: (slotRef: string, assignmentId: string | null) => void;
  /**
   * ⚠️ [UX1 §3c] WHOSE QUESTION THE POST'S DOT ANSWERS. `web/src/lib/accessRole.ts`'s
   * `fillAudienceFor` is the one derivation; BOTH hosts call it, so the same person cannot see
   * one post green here and amber two clicks away.
   */
  audience: PostAudience;
  /**
   * ⚠️ [P7A §4] WHICH ADDRESSEES DECLARED THEIR SHARE SHORT, AND WHY — keyed by the ADDRESSEE
   * (`closedShortReasonsOf` in `lib/shortfallNote.ts`, the same derivation the fill screen's excused
   * set uses). A row whose addressee closed short and is still short states it, with the reason, so a
   * declared `0 of 12` no longer reads exactly like an untouched one.
   *
   * ⚠️ REQUIRED, NOT DEFAULTED — `audience`'s rule. A default of "nobody declared" would put one
   * reading in the omitted position and a host that forgot it would silently say nothing; a host
   * that states the close somewhere else hands `NO_CLOSED_SHORT` on purpose.
   */
  closedShortReasons: ReadonlyMap<string, string>;
  /**
   * [C1f §1a] Which subjects are showing DELEGATE rather than NAME, keyed by
   * `<lineKey>|<allocKey>`. Mode is the host's state because the host owns the write.
   */
  postMode?: ReadonlyMap<string, PostMode>;
  /**
   * [C1f §1a/§1c] `undefined` renders NO control at all — which is what a read-only sheet
   * gets, what a UNIT nodal gets, and what every consumer that is not the fill screen gets.
   */
  onPostMode?: (lineKey: string, allocKey: string, mode: PostMode) => void;
  /** [C1f §1d] The host's inline delegate row, so this file keeps holding no mutation. */
  renderDelegate?: (lineKey: string, allocKey: string) => React.ReactNode;
  /**
   * ⚠️ [C2a §5e] REMOVE ONE UNIT FROM A SAVED SPLIT. The host owns the write, as with every
   * other verb on this screen — this file still holds no mutation.
   *
   * `undefined` renders no control at all, which is how a read-only sheet and a UNIT nodal
   * get nothing (`onPostMode`'s rule, one prop up).
   */
  onDelegationRemove?: (lineKey: string, sourceAllocKey: string, unitOrgUnitId: string) => void;
};

const subjectOf = (lineKey: string, allocKey: string): string => `${lineKey}|${allocKey}`;

export const FillLines = ({
  lines,
  staged,
  cursor,
  controls,
  onAdd,
  onSwap,
  onRemove,
  audience,
  closedShortReasons,
  postMode,
  onPostMode,
  renderDelegate,
  onDelegationRemove,
}: FillLinesProps) => {
  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [PRE-P6.1] ONE HOVER CARD FOR THE WHOLE SCREEN, OWNED HERE.
   *
   *  Divergence #82 measured what an overlay PER ROW costs — an rc-trigger and a
   *  `ResizeObserver` each, 64.25s to 119.18s on one test file — and this component renders up
   *  to twelve chips per line and several lines per sheet. Only one card can be visible, so
   *  only one is mounted; the chips report their own hover and this decides which officer it
   *  describes.
   *
   *  ⚠️ IT STAYS HERE THROUGH C1f's SPLIT INTO POSTS AND ROWS. Pushing it down to the row
   *  would put one card per addressee back, which is the measurement above with more rows.
   *
   *  ⚠️ THE ELEMENT IS HELD, NOT THE RECTANGLE. A rectangle captured on enter goes stale the
   *  moment the sheet scrolls under a still-hovered chip; reading it during render is correct
   *  for as long as the card is up, and the pointer leaving is what takes it down.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const [hovered, setHovered] = useState<{
    element: HTMLElement;
    officer: OfficerSummaryDto;
    attendance?: string;
  } | null>(null);
  /* [C1f §1h] Which collapsed unit lines the operator has opened, by `<lineKey>|<allocKey>`. */
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const anchor = hovered?.element.getBoundingClientRect();

  const renderAllocation = (line: FillLineDto, allocation: FillAllocationDto, control: React.ReactNode) => {
    const named = allocation.slots.filter((slot) => slot.assignment?.officer || staged.has(slot.slotRef)).length;
    const subject = subjectOf(line.lineKey, allocation.allocKey);

    /*
     * ─────────────────────────────────────────────────────────────────────────────────
     *  ⚠️ [C2a §5c/§5d/§5e] A DELEGATED ROW SAYS SO, AND CARRIES ITS OWN EDIT AND REMOVE.
     *
     *  Until now a handed-down row was indistinguishable from one still waiting for names —
     *  its ONLY difference was that it collapsed past four slots — so an operator could not
     *  tell what was still theirs to do. And the only way to undo a split was to re-open the
     *  SOURCE row's form and type 0, which is a side effect somebody discovered rather than an
     *  affordance anybody designed.
     *
     *  ⚠️ NO NEW COLOUR MEANING (§5c). The marker is `text-muted-foreground`, the same neutral
     *  every secondary count on this screen uses. Red-amber-green on this screen means FILL
     *  STATE and nothing else — [C1f §1]'s "one dot per post" is the whole of the colour here,
     *  and a second hue would make "delegated" compete with "unstaffed" for the same glance.
     *
     *  ⚠️ AND IT ADDS NO `title` ATTRIBUTE BEFORE THE SCOPE CHIP. `fillGaps.spec.ts` finds that
     *  chip with `node.querySelector('[title]')` — the FIRST one in the row — so a `title` here
     *  would silently re-point two geometry assertions at the wrong box. The marker is plain
     *  text; `AddresseeCodes` keeps the only `title` in the row.
     *
     *  ⚠️ THE CONTROLS ACT ON THE **PARENT** KEY, WHICH IS THE ROW THE SPLIT LIVES ON.
     *  `POST /duties/:id/delegations` REPLACES a source row's whole split, so both verbs are
     *  expressed as "the same split, minus this unit" and are addressed to
     *  `delegatedFromAllocKey`.
     *
     *  ─── ⚠️ [amended C2b §7c] AND THE PARENT ROW NO LONGER HAS TO EXIST ──────────────────
     *
     *  This used to require it. `delegateAllocation` writes the retained source only when
     *  `plan.retained > 0`, so a post delegated in FULL left no source row, and
     *  `requireDelegationContext` answered 404 for the key — in BOTH directions, so the split
     *  could be neither edited nor undone and the only way back was to edit the order.
     *  [UX1 §2] recorded that as an accepted cost and [C2a] carried it as an open question.
     *
     *  §7c makes the server tolerate a source row that no longer exists — admitted only when a
     *  surviving child still names the key as its parent, and re-authorised against the org
     *  tree because the parent's SHARE is deleted along with its allocation. So the controls
     *  are offered whenever the row is delegated at all, and `splitFinal` is gone with the
     *  limitation it described.
     * ─────────────────────────────────────────────────────────────────────────────────
     */
    const parentKey = allocation.delegatedFromAllocKey;
    const mayEdit = parentKey !== null && onPostMode !== undefined && controls.mayDelegate;
    /*
     * ⚠️ [C1h §3a] MORE THAN ONE ADDRESSEE MEANS THE PER-UNIT NUMBERS DIFFER FROM THE POST'S.
     *
     * On a single-addressee post they are the SAME NUMBER, and §3a's whole complaint is that
     * the duplicate cost a whole line per row and carried nothing. A split post (§3f) keeps
     * its one-line-per-unit body, because there the counts are genuinely different facts.
     */
    const perUnit = line.allocations.length > 1;

    /*
     * ⚠️ [C1f §1h] THE COLLAPSE IS SCOPED TO A **DELEGATED** UNIT LINE, and that is not a
     * detail. §1e is what multiplies the rows, so §1e's rows are what must be shorter — and
     * collapsing an ordinary twelve-slot allocation would take every `slot-add` button off
     * the screen, which is the population `DutyFill.test.tsx`'s twelve-name click budget is
     * measured on.
     *
     * ⚠️ AND IT IS FORCE-OPEN WHERE THE OPERATOR IS WORKING. Without the cursor clause the
     * search sheet opens on a slot with no visible chip; without the staged clause a
     * copy-from-last-time proposal is invisible on the very row it filled.
     */
    /* ⚠️ [P7A §4] This row's declared close, only while it is still short — see the line it renders. */
    const closedShort = allocationClosedShort(closedShortReasons, allocation, named);
    const holdsStaged = allocation.slots.some((slot) => staged.has(slot.slotRef));
    const collapsible = allocation.delegatedFromAllocKey !== null && allocation.slots.length > UNIT_LINE_CHIP_CAP;
    const collapsed = collapsible && !opened.has(subject) && cursor?.allocKey !== allocation.allocKey && !holdsStaged;

    return (
      <div
        key={subject}
        /*
         * ⚠️ [D4.5 §3f] A NAMED CONTAINER, because the chip grid below measures ITSELF.
         * Unnamed, its `@min-` thresholds would resolve to `<main>` — 1062px at the 1366
         * floor while this row has far less beside a label column — and the chips would pack
         * to a width the row does not have.
         *
         * ⚠️ [amended C1f] `fill-line` AND `@container/fillline` STAY ON THE ELEMENT THAT
         * HOLDS `fill-chips`. `fillGaps.spec.ts` reaches for the chip grid THROUGH this node,
         * so naming the post section `fill-line` would make every measured row `null` and the
         * spec would pass over nothing.
         */
        className="@container/fillline flex flex-col gap-2 border-b border-border py-3"
        data-testid="fill-line"
        data-alloc-key={allocation.allocKey}
      >
        {/*
          ─────────────────────────────────────────────────────────────────────────
           ⚠️ [C1h §3, amended UX1 §1a/§1b] THE UNIT ROW'S ANATOMY, CORRECTED.

           [C1h] put the scope chip and the count on a header line and indented the slots
           beneath it, which left the band beside the grid empty and the row looking hollow.
           §1a moves the CHIP down onto the slots line, where it fills that band; §1b moves
           the ACTION up beside the post's name. What is left here is the per-unit COUNT, and
           it keeps its right alignment.

           ⚠️ IT STILL RENDERS ONLY WHEN THE POST HAS MORE THAN ONE ADDRESSEE. §3a: on a
           single-addressee post the unit count is the SAME NUMBER as the post header's, and a
           whole line per row carrying no information is exactly what §3a removed.

           ⚠️ AND THE ACTION IS THE LAST CHILD OF THE IDENTITY CLUSTER IN BOTH BRANCHES — the
           post's header for a single-addressee post, this row for a split one — so [C1h §3c]'s
           "the action never migrates" still holds, and the COUNT is now the row's right edge
           in every case, which is what `fillGaps.spec.ts` measures.
          ─────────────────────────────────────────────────────────────────────────
        */}
        {perUnit ? (
          <div className={cn('flex items-center gap-3', control ? 'justify-between' : 'justify-end')}>
            {control}
            {/* ⚠️ [amended C1f] Muted, because the post header above owns the colour. */}
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="alloc-count">
              {POST_COPY.namedOf(named, allocation.allocatedCount)}
            </span>
          </div>
        ) : null}

        {/* ⚠️ [C2a §5c/§5d/§5e] See `renderAllocation`'s header. Plain text, no `title`. */}
        {parentKey !== null ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="delegated-marker">
            <span className="text-xs text-muted-foreground">{POST_COPY.delegatedFrom}</span>
            {mayEdit ? (
              <>
                <button
                  type="button"
                  className="tap-target text-xs text-link underline-offset-2 hover:underline"
                  data-testid={`delegation-edit-${allocation.allocKey}`}
                  aria-label={POST_COPY.editSplitFor(line.label)}
                  onClick={() => {
                    onPostMode?.(line.lineKey, parentKey, 'delegate');
                  }}
                >
                  {POST_COPY.editSplit}
                </button>
                {onDelegationRemove && allocation.unit ? (
                  <button
                    type="button"
                    className="tap-target text-xs text-destructive underline-offset-2 hover:underline"
                    data-testid={`delegation-remove-${allocation.allocKey}`}
                    aria-label={POST_COPY.removeSplitFor(allocation.unit.name, line.label)}
                    onClick={() => {
                      onDelegationRemove(line.lineKey, parentKey, allocation.unit?.id ?? '');
                    }}
                  >
                    {POST_COPY.removeSplit}
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}

        <div className={SLOT_ROW}>
          {/*
            ⚠️ [UX1 §1a] THE CHIP, ON THE SLOTS LINE, AT ITS LEFT. See `SLOT_ROW` above for why
            there is no padding number any more.

            ⚠️ [D4.4 §1b] THE DIVISION IS ALWAYS STATED — it used to print the unit's name only
            when a line named one, so the ordinary division-only allocation, the common case,
            rendered as nothing at all.

            ⚠️ [amended C1f] AND THE POST'S LABEL IS NOT HERE. It is on the section header
            exactly once; on both, `findByText('UB Men')` reports "found multiple elements".

            ⚠️ [UX1 §1e] ITS HOVER IS A NATIVE `title` CARRYING THE FULL PATH, inside
            `AddresseeCodes`. Never an AntD `Tooltip` — measured at divergence #82, one per
            allocation row took a test file from 64s to 119s.
          */}
          <AddresseeCodes className={SCOPE_CHIP} division={allocation.division} unit={allocation.unit} />

          {collapsed ? (
            /* ⚠️ [C1f §1h] A `<button>` WITH `aria-expanded`, never a tooltip and never a link
               that does nothing — `ChipList`'s rule, because hover is unavailable on touch and
               unreachable from a keyboard. It carries the shortfall, so nothing renders twice. */
            <button
              type="button"
              aria-expanded={false}
              data-testid="unit-slots-toggle"
              className="tap-target self-start text-sm text-link underline-offset-2 hover:underline"
              onClick={() => {
                setOpened((current) => new Set([...current, subject]));
              }}
            >
              {POST_COPY.shortfall(named, allocation.allocatedCount)}
            </button>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {/*
                ─────────────────────────────────────────────────────────────────────────
                 ⚠️ [D4.5 §3f] AT MOST SIX CHIPS TO A LINE, AND A `flex-wrap` CANNOT SAY THAT.
                 A GRID states the cap directly: the column count IS the rule, and it steps down
                 as the container narrows so the chips never squeeze instead of wrapping.

                 ⚠️ A CONTAINER QUERY, NEVER `sm:`/`md:` (CLAUDE.md §8) — `vitest.setup.ts` stubs
                 `matchMedia` to `matches: false` for ever, so a width-branching RENDER would
                 silently take the narrow path in every test.

                 ⚠️ [PRE-P6 §2e] SIX, NOT TEN. At ten columns a 928px row gave each chip about
                 85px — less than the chip's own non-shrinkable content, so it overflowed its
                 cell and its `⋮` sat over the next slot.

                 ⚠️ [UX1 §1a] THE THRESHOLDS DO NOT MOVE, AND THAT IS CHECKED RATHER THAN HOPED.
                 The grid's available width goes from `row − 184px` to `row − 172px` — 12px WIDER
                 at every rung — so no chip is narrower than it was and the 85px overflow above
                 cannot be reached from this change.
                ─────────────────────────────────────────────────────────────────────────
              */}
              <div
                data-testid="fill-chips"
                className="grid min-w-0 grid-cols-1 justify-items-start gap-2 @min-[320px]/fillline:grid-cols-2 @min-[480px]/fillline:grid-cols-3 @min-[640px]/fillline:grid-cols-4 @min-[800px]/fillline:grid-cols-5 @min-[960px]/fillline:grid-cols-6"
              >
                {allocation.slots.map((slot, index) => {
                  const stagedOfficer = staged.get(slot.slotRef) ?? null;
                  const officer = slot.assignment?.officer ?? stagedOfficer;
                  /* [C5c §5a] The chip's own word for the hover card — `SlotChip`'s, through one function. */
                  const attendanceWord = slot.attendance === undefined ? null : presenceWord(slot.attendance, true);

                  return (
                    <SlotChip
                      key={slot.slotRef}
                      lineLabel={line.label}
                      index={index}
                      total={allocation.slots.length}
                      officer={officer}
                      state={slot.assignment?.officer ? 'filled' : stagedOfficer ? 'staged' : 'empty'}
                      active={cursor?.slotRef === slot.slotRef}
                      controls={controls}
                      /* ⚠️ [C3c §7] Absent on a live duty — the key's presence is the signal. */
                      attendance={slot.attendance}
                      onAdd={() => onAdd(line.lineKey, allocation.allocKey, slot.slotRef)}
                      onSwap={() => onSwap(line.lineKey, allocation.allocKey, slot.slotRef)}
                      onRemove={() => onRemove(slot.slotRef, slot.assignment?.id ?? null)}
                      /* ⚠️ ONLY A NAMED CHIP DESCRIBES ANYBODY. An empty slot has no officer,
                         and a staged one is a proposal nobody has saved — both are reported so
                         that moving off a named chip still closes the card. */
                      onHoverChange={(element) => {
                        setHovered(
                          element && officer
                            ? /* ⚠️ [amended C3c.1] THE VISIBLE HALF OF §7's word. The chip carries
                                 it `sr-only` and no `title` ([PRE-P6.1]: a native tooltip racing
                                 this card is two answers to one gesture), so the card is where a
                                 sighted operator reads it. Absent on a live duty. */
                              {
                                element,
                                officer,
                                ...(attendanceWord === null ? {} : { attendance: attendanceWord }),
                              }
                            : null,
                        );
                      }}
                      describedBy={hovered?.officer.id === officer?.id ? OFFICER_HOVER_CARD_ID : undefined}
                    />
                  );
                })}
              </div>
              {collapsible ? (
                <button
                  type="button"
                  aria-expanded
                  data-testid="unit-slots-toggle"
                  className="tap-target shrink-0 self-start text-xs text-link underline-offset-2 hover:underline"
                  onClick={() => {
                    setOpened((current) => new Set([...current].filter((key) => key !== subject)));
                  }}
                >
                  {POST_COPY.collapse}
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/*
          ─────────────────────────────────────────────────────────────────────────
           ⚠️ [P7A §4] NOT ASSIGNED IS NOT THE SAME AS SHORT — STATED ON THE ROW THE DECLARATION IS ABOUT.

           The post's dot stays what it is: the post IS unstaffed, and [C5b §3] keeps a declared
           close red or amber with the lock on the footer. What was missing is that THIS addressee
           has replied — a division that can still be chased and one that has said why it cannot
           read exactly alike. A muted line, beneath the slots, with the division's own words.

           ⚠️ NO NEW HUE, NO `title` AND THE LAST CHILD. Colour on this screen is fill state alone
           (§5c); `fillGaps.spec.ts` finds the scope chip as the row's FIRST `[title]`; and below the
           slots it moves neither the chip nor the grid that spec measures. It never names who
           declared — `completeShares` closes every open share its caller sees, so it may have been
           closed from above. Shown on a collapsed delegated row too: the reason is not a slot.
          ─────────────────────────────────────────────────────────────────────────
        */}
        {closedShort === null ? null : (
          <p
            data-testid="alloc-closed-short"
            className="flex items-start gap-1.5 text-xs break-words text-muted-foreground"
          >
            <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
            <span>{closedShortRowNote(closedShort)}</span>
          </p>
        )}
      </div>
    );
  };

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C2a §5a] A VEHICLE COLUMN IS A COUNT. IT OFFERS NO OFFICER ANYWHERE.
   *
   *  `kind` has been on the wire since [P4] and this screen read it nowhere, so a `V:` line
   *  rendered as a person: an officer avatar, "Add Van, slot 2 of 4", a hover card carrying
   *  somebody's PHONE NUMBER, and "Any rank" in the search sheet — because a vehicle's
   *  `eligibleRanks` is `[]`, which means NO RESTRICTION rather than no people.
   *
   *  ⚠️ IT READS `kind`, NEVER THE `V:` KEY PREFIX AND NEVER THE LABEL. The prefix is how
   *  `fillRef.util.ts` MINTS a key; `kind` is what the line IS, resolved server-side from the
   *  vehicle-type master. Parsing the key here would be the identity-from-a-position family
   *  §7 has corrected four times.
   *
   *  ⚠️ ITS OWN TEST IDS, RATHER THAN `fill-line`/`fill-chips`. §5h requires those two to stay
   *  on the element holding the chip grid, and a vehicle row HAS no grid — so reusing them
   *  would put rows into `fillGaps.spec.ts`'s measurements that its null-filters would then
   *  silently drop, which is how a geometry spec starts passing over nothing.
   *
   *  ⚠️ AND NO `StatusDot` AND NO Name/Delegate CONTROL. Red-amber-green answers "is this
   *  staffed", which a vehicle cannot be, and `POST_COPY.modeLabel` reads "Who fills …" —
   *  person language for a thing that is not one. Stated as a decision: if delegating vehicles
   *  is wanted it is a client question, not an omission to be quietly filled in.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const renderVehicle = (line: FillLineDto) => {
    const asked = line.allocations.reduce((total, allocation) => total + allocation.allocatedCount, 0);
    const perUnit = line.allocations.length > 1;

    return (
      <section key={line.lineKey} data-testid="fill-vehicle" data-line-key={line.lineKey}>
        <div className="flex flex-wrap items-center justify-start gap-3 pt-3">
          {/*
            ⚠️ DECORATIVE, SO `aria-hidden`. The label beside it already says which vehicle this
            is, and `CoverageBar`'s R2 is the rule: the graphic is hidden and the SENTENCE is the
            content. An icon with no accessible name and no `aria-hidden` is announced as an
            unnamed graphic before the text that actually answers the question.
          */}
          <Van className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 truncate text-sm font-medium">{line.label}</span>
          {/*
            ⚠️ THE ZERO CASE IS ITS OWN SENTENCE, NOT A ZERO. `Police Van - 0` reads as a
            requirement that was asked for and not met; `None required` is what is true. Both
            strings live in `POST_COPY`, so neither is a literal here and neither goes dead.
            ⚠️ [P7A §3] `N required` — WHAT IT REQUIRES, AND NOTHING ELSE. A vehicle is never short
            and never named, so the line carries no second number and no tone.
          */}
          <span
            className="shrink-0 text-sm font-semibold tabular-nums text-secondary-foreground"
            data-testid="vehicle-total"
          >
            {asked > 0 ? POST_COPY.vehicleCount(asked) : POST_COPY.vehicleNothing}
          </span>
          {/* ⚠️ [P7A §3] Where a post lists who is named: there is no vehicle mapping to show. */}
          <span data-testid="vehicle-no-mapping" className="text-xs text-muted-foreground">
            {POST_COPY.vehicleNoMapping}
          </span>
        </div>

        {line.allocations.map((allocation) => (
          <div
            key={subjectOf(line.lineKey, allocation.allocKey)}
            className="flex items-center gap-3 border-b border-border py-3"
            data-testid="fill-vehicle-line"
            data-alloc-key={allocation.allocKey}
          >
            <AddresseeCodes className={SCOPE_CHIP} division={allocation.division} unit={allocation.unit} />
            {perUnit ? (
              <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="vehicle-alloc-count">
                {POST_COPY.vehicleCount(allocation.allocatedCount)}
              </span>
            ) : null}
          </div>
        ))}
      </section>
    );
  };

  return (
    <div className="flex flex-col" data-testid="fill-lines">
      {[...lines]
        .sort((left, right) => left.displayOrder - right.displayOrder)
        .map((line) => {
          /* ⚠️ [C2a §5a] Before anything person-shaped is computed for it. */
          if (line.kind === 'vehicle') return renderVehicle(line);

          const summary = postSummary(line, new Set(staged.keys()), audience);
          /*
           * ⚠️ [UX1 §2] TWO FACTS, TWO GRAINS — AND THIS REVERSES [C1f §1b].
           *
           * That pass offered the control only while the post was UNTOUCHED (no names AND no
           * delegations), which its own predicate's header predicted would be the thing a
           * "change the split" affordance changed. The client has asked for it. What replaces
           * it is two questions on two grains, because one predicate cannot answer both:
           *
           *   POST       is there capacity left to route? (`postHasRemainder`)
           *   ALLOCATION is THIS row addressed to `Any`?  (`allocationMayBeSplit`)
           *
           * ⚠️ §2b IS ABSENCE, NOT DISABLEMENT. `CD · E1` is already at a unit and there is
           * nothing for the operator to decide there; a disabled control would promise there
           * was.
           *
           * ⚠️ AND THE COST OF §2, STATED WHERE IT BITES: a post delegated in FULL cannot be
           * re-opened from this screen. `delegateAllocation` writes the retained source row
           * only when `plan.retained > 0`, so a post delegated in full leaves no `CD · Any`
           * allocation on the line for the control to sit on — and `requireDelegationContext`
           * answers 404 for the alloc key that is no longer there. Splitting SOME of a post is
           * now freely editable; splitting ALL of it is final from here.
           */
          const remains = onPostMode !== undefined && postHasRemainder(summary);
          /*
           * ⚠️ [amended C2a §5d] A ROW THAT ALREADY HOLDS A SPLIT IS RE-OPENABLE EVEN WITH NO
           * REMAINDER LEFT.
           *
           * [UX1 §2]'s gate is "is there capacity left to route?", which is the right question
           * for OFFERING a new split and the wrong one for CHANGING a saved one: once every
           * slot is named, `postHasRemainder` is false and the existing split became
           * uneditable — the exact dead end §5d/§5e are about. Editing an existing split moves
           * capacity between rows and adds none, so the remainder is not what governs it.
           *
           * ⚠️ IT STILL REQUIRES `allocationMayBeSplit` — only an `Any`-addressed row hosts a
           * split, so a handed-down row is excluded by construction and [C1c §7]'s one-level
           * rule is untouched.
           */
          const hasSplit = (allocation: FillAllocationDto): boolean =>
            line.allocations.some((row) => row.delegatedFromAllocKey === allocation.allocKey);
          const offeredFor = (allocation: FillAllocationDto): boolean =>
            onPostMode !== undefined && allocationMayBeSplit(allocation) && (remains || hasSplit(allocation));
          /*
           * ─────────────────────────────────────────────────────────────────────────────────
           *  ⚠️ [C1f] THE CONTROL'S SUBJECT IS ONE ALLOCATION, AND ITS PLACEMENT IS A STATE
           *  BRANCH. UNCHANGED RULE, CORRECTED PREDICATE.
           *
           *  It read `line.allocations.length === 1`, which asks how many rows the post HAS.
           *  The question the rule is actually about is how many rows the control could ACT ON
           *  — and since [UX1 §2b] those are different numbers: only an `Any` row is offerable,
           *  so every delegated child is excluded by construction.
           *
           *  ⚠️ THE COST OF THE OLD FORM WAS A WHOLE ROW PER SPLIT POST. `CD · Any` retained
           *  beside `CD · E1` delegated is TWO allocations and exactly ONE of them is offerable,
           *  so the control had a single unambiguous subject and was being pushed onto the
           *  addressee row anyway — a line of chrome for a post that had already been split
           *  once. It now sits beside the post name, which is where §1b put it for the
           *  unsplit case and where it belongs in both.
           *
           *  ⚠️ AND THE CP-OFFICE CASE IS UNCHANGED, WHICH IS THE HALF WORTH KEEPING. A post
           *  asking `CD · Any` AND `ND · Any` has TWO offerable rows, "Delegate" has no single
           *  subject, and the control drops onto the addressee rows — which do. That is the
           *  case [C1f] wrote the rule for, and it is the one this predicate still answers.
           * ─────────────────────────────────────────────────────────────────────────────────
           */
          /*
           * ─────────────────────────────────────────────────────────────────────────────
           *  ⚠️ [amended C2a §5f] ONE PLACEMENT. THE FORM ALWAYS OPENS UNDER THE POST HEADER.
           *
           *  It used to sit under the header when exactly ONE allocation was offerable and
           *  under the allocation's own row otherwise — so on a CP-office post asking
           *  `CD · Any` AND `ND · Any` the form appeared in a different place than it did on
           *  every other post, and two visually identical rows opened it in two positions.
           *  §5f asks for one, and the header is the one that exists on every post.
           *
           *  ⚠️ WHICH ROW IT IS ABOUT IS NOW CARRIED BY THE MODE MAP RATHER THAN BY POSITION —
           *  `postMode` is keyed `<lineKey>|<allocKey>`, so a form opened from the second `Any`
           *  row is still that row's form, it simply renders in the post's one slot.
           *
           *  ⚠️ AND THE `Delegate` TOGGLE ITSELF DOES NOT MOVE. It stays beside the post name
           *  for a single offerable row and on the addressee row when there are several —
           *  [C1h §3c]'s "the action never migrates" — because that control IS the row's, and
           *  only the FORM had two homes.
           * ─────────────────────────────────────────────────────────────────────────────
           */
          const offerable = line.allocations.filter(offeredFor);
          const headerSubject = offerable.length === 1 ? offerable[0] : undefined;
          const onHeader = headerSubject !== undefined;
          /* The row whose form is currently open, wherever its toggle lives. */
          const openFor = line.allocations.find(
            (allocation) =>
              offeredFor(allocation) && postMode?.get(subjectOf(line.lineKey, allocation.allocKey)) === 'delegate',
          );

          const controlFor = (allocation: FillAllocationDto) => {
            if (!offeredFor(allocation) || !onPostMode) return null;
            const subject = subjectOf(line.lineKey, allocation.allocKey);
            const mode = postMode?.get(subject) ?? 'name';
            return (
              <PostModeControl
                postLabel={line.label}
                value={mode}
                data-testid={`post-mode-${allocation.allocKey}`}
                onChange={(next) => {
                  onPostMode(line.lineKey, allocation.allocKey, next);
                }}
              />
            );
          };

          const delegatingFor = (allocation: FillAllocationDto): React.ReactNode => {
            if (!offeredFor(allocation) || !renderDelegate) return null;
            if (postMode?.get(subjectOf(line.lineKey, allocation.allocKey)) !== 'delegate') return null;
            return renderDelegate(line.lineKey, allocation.allocKey);
          };

          return (
            <section key={line.lineKey} data-testid="fill-post" data-line-key={line.lineKey}>
              {/*
                ─────────────────────────────────────────────────────────────────────────
                 ⚠️ [C1h §3, amended UX1 §1b] IDENTITY AND ACTION LEFT, ONE COUNT RIGHT.

                 [C1h] put the scope chip beside the name and the action at the far right. §1a
                 moves the chip DOWN onto the slots line, where it fills the band the indent
                 left empty; §1b gives the action the position the chip has vacated, beside the
                 name, WHERE IT APPLIES.

                 ⚠️ [amended C3a §5a] THE COUNT MOVED FROM THE RIGHT-HAND EDGE INTO THE HEADING,
                 IN BRACKETS — `Sub-Inspector / Sergeant (2 of 4 named)`. §1b's "one count right"
                 is therefore superseded, and `justify-between` went with it: there is nothing on
                 that side any more.

                 ⚠️ COLOUR AND COUNT STAY **ONE NODE**. The `StatusDot` moved WHOLE rather than
                 being split into a dot here and a number there — [C1f §1]'s "ONE `StatusDot` PER
                 LINE, and that is the whole of the colour on this screen" is what makes a split
                 post add no colour, and two nodes would also break `FillLines.test.tsx`'s
                 exactly-one assertion for a reason that has nothing to do with what it guards.

                 ⚠️ AND THE ACTION IS STILL THE CLUSTER'S LAST CHILD — [C1h §3c]'s "the action
                 never migrates". The count is inserted BEFORE it, not after.
                ─────────────────────────────────────────────────────────────────────────
              */}
              <div className="flex flex-wrap items-center gap-3 pt-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium">{line.label}</span>
                  {/*
                    ⚠️ [C1f §1g] THE ONE DOT, AND `postFillStateOf` IS WHAT DECIDES IT.

                    ⚠️ [amended C5b §3] AND THE TONE IS `postDotTone`, `fillTone`'s post twin — red only
                    where the post asks for people. A post never reaches COMPLETE, so a declared close
                    keeps its post red or amber; the lock lives on the footer. [amended P7A §4] The
                    close itself is stated on the ROW it is about (`alloc-closed-short`, with the
                    reason) — the dot still states staffing, which a declaration does not change.

                    ⚠️ [amended C4a §6] THE LABEL IS COMPONENTS, NEVER A COMPOSITE. The addressee
                    reads `3 named · 1 delegated · 2 to go`; oversight reads `3 named · 3 not yet
                    named`. `postHeadingCounts` is the one derivation and the tone is unchanged,
                    so `to go` reaching zero and the dot going green are one fact — and oversight
                    still never goes green on a delegation ([UX1 §3b]). [C3a §5c]'s
                    `2 of 4 delegated · 2 named` is gone: its two clauses overlapped.
                  */}
                  <StatusDot
                    data-testid="post-tone"
                    className="shrink-0"
                    tone={postDotTone(summary)}
                    label={POST_COPY.bracketed(POST_COPY.postCount(postHeadingCounts(summary, audience)))}
                  />
                  {/*
                    ⚠️ [C5c §2h] THE ANCHOR'S MARKER — AFTER THE DOT, SO `fillGaps.spec.ts`' left-half
                    tone geometry is unchanged, and WITHOUT a `title`, because that spec finds the
                    scope chip as the row's first `[title]`. Muted: colour here means fill state.
                  */}
                  {line.shared ? (
                    <span data-testid="post-shared" className="shrink-0 text-xs text-muted-foreground">
                      {POST_COPY.sharedAcross}
                    </span>
                  ) : null}
                  {headerSubject ? controlFor(headerSubject) : null}
                </div>
              </div>

              {openFor ? <div className="pt-2">{delegatingFor(openFor)}</div> : null}

              {line.allocations.map((allocation) => (
                <div key={subjectOf(line.lineKey, allocation.allocKey)}>
                  {renderAllocation(line, allocation, onHeader ? null : controlFor(allocation))}
                  {/* ⚠️ [C2a §5f] The form no longer renders here — see `openFor` above. */}
                </div>
              ))}
            </section>
          );
        })}

      {hovered && anchor ? (
        <OfficerHoverCard officer={hovered.officer} anchor={anchor} attendance={hovered.attendance} />
      ) : null}
    </div>
  );
};
